'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface CalculatorInputs {
  industry: string;
  companySize: string;
  currentAICosts: string;
  employeeCount: string;
  avgSalary: string;
}

interface ROICalculatorProps {
  className?: string;
}

const ROICalculator = ({ className = '' }: ROICalculatorProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [inputs, setInputs] = useState<CalculatorInputs>({
    industry: 'financial-services',
    companySize: 'enterprise',
    currentAICosts: '',
    employeeCount: '',
    avgSalary: '',
  });
  const [results, setResults] = useState<any>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return (
      <div className={`bg-card rounded-lg shadow-subtle p-8 ${className}`}>
        <div className="animate-pulse">
          <div className="h-8 bg-muted rounded w-1/3 mb-6"></div>
          <div className="space-y-4">
            <div className="h-20 bg-muted rounded"></div>
            <div className="h-20 bg-muted rounded"></div>
            <div className="h-20 bg-muted rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  const calculateROI = () => {
    const costs = parseFloat(inputs.currentAICosts) || 0;
    const employees = parseFloat(inputs.employeeCount) || 0;
    const salary = parseFloat(inputs.avgSalary) || 0;

    const industryMultipliers: { [key: string]: number } = {
      'financial-services': 3.5,
      'healthcare': 3.2,
      'retail': 2.8,
      'manufacturing': 3.0,
      'technology': 3.8,
    };

    const multiplier = industryMultipliers[inputs.industry] || 3.0;
    
    const efficiencyGains = employees * salary * 0.15;
    const costReduction = costs * 0.25;
    const riskMitigation = costs * 0.10;
    
    const totalBenefit = efficiencyGains + costReduction + riskMitigation;
    const implementationCost = costs * 0.30;
    const netBenefit = totalBenefit - implementationCost;
    const roi = ((netBenefit / implementationCost) * 100).toFixed(1);
    const paybackMonths = ((implementationCost / (totalBenefit / 12))).toFixed(1);

    setResults({
      totalBenefit: totalBenefit.toFixed(0),
      implementationCost: implementationCost.toFixed(0),
      netBenefit: netBenefit.toFixed(0),
      roi,
      paybackMonths,
      efficiencyGains: efficiencyGains.toFixed(0),
      costReduction: costReduction.toFixed(0),
      riskMitigation: riskMitigation.toFixed(0),
    });
  };

  const handleInputChange = (field: keyof CalculatorInputs, value: string) => {
    setInputs(prev => ({ ...prev, [field]: value }));
    setResults(null);
  };

  return (
    <div className={`bg-card rounded-lg shadow-subtle p-8 ${className}`}>
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
          <Icon name="CalculatorIcon" size={24} className="text-primary" />
        </div>
        <div>
          <h3 className="text-2xl font-display font-semibold text-foreground">
            ROI Calculator
          </h3>
          <p className="text-sm text-muted-foreground">
            Estimate your potential return on investment
          </p>
        </div>
      </div>

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Industry
          </label>
          <select
            value={inputs.industry}
            onChange={(e) => handleInputChange('industry', e.target.value)}
            className="w-full px-4 py-2.5 bg-background border border-input rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-200"
          >
            <option value="financial-services">Financial Services</option>
            <option value="healthcare">Healthcare</option>
            <option value="retail">Retail & E-commerce</option>
            <option value="manufacturing">Manufacturing</option>
            <option value="technology">Technology</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Company Size
          </label>
          <select
            value={inputs.companySize}
            onChange={(e) => handleInputChange('companySize', e.target.value)}
            className="w-full px-4 py-2.5 bg-background border border-input rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-200"
          >
            <option value="mid-market">Mid-Market (500-2,000 employees)</option>
            <option value="enterprise">Enterprise (2,000+ employees)</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Current Annual AI/Tech Costs (AUD)
          </label>
          <input
            type="number"
            value={inputs.currentAICosts}
            onChange={(e) => handleInputChange('currentAICosts', e.target.value)}
            placeholder="e.g., 500000"
            className="w-full px-4 py-2.5 bg-background border border-input rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-200"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Number of Employees Affected
          </label>
          <input
            type="number"
            value={inputs.employeeCount}
            onChange={(e) => handleInputChange('employeeCount', e.target.value)}
            placeholder="e.g., 250"
            className="w-full px-4 py-2.5 bg-background border border-input rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-200"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Average Annual Salary (AUD)
          </label>
          <input
            type="number"
            value={inputs.avgSalary}
            onChange={(e) => handleInputChange('avgSalary', e.target.value)}
            placeholder="e.g., 85000"
            className="w-full px-4 py-2.5 bg-background border border-input rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-200"
          />
        </div>

        <button
          onClick={calculateROI}
          disabled={!inputs.currentAICosts || !inputs.employeeCount || !inputs.avgSalary}
          className="w-full px-6 py-3 bg-accent text-accent-foreground font-display font-medium rounded-lg hover:shadow-authority transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
        >
          Calculate ROI
        </button>
      </div>

      {results && (
        <div className="mt-8 pt-8 border-t border-border">
          <h4 className="text-lg font-display font-semibold text-foreground mb-6">
            Your Projected Results
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-success/10 rounded-lg p-4">
              <div className="text-3xl font-display font-semibold text-success mb-1">
                {results.roi}%
              </div>
              <div className="text-sm text-muted-foreground">
                Return on Investment
              </div>
            </div>
            
            <div className="bg-primary/10 rounded-lg p-4">
              <div className="text-3xl font-display font-semibold text-primary mb-1">
                {results.paybackMonths}
              </div>
              <div className="text-sm text-muted-foreground">
                Months to Payback
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <span className="text-sm text-foreground">Total Annual Benefit</span>
              <span className="text-sm font-medium text-foreground">
                ${parseInt(results.totalBenefit).toLocaleString()}
              </span>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <span className="text-sm text-foreground">Implementation Cost</span>
              <span className="text-sm font-medium text-foreground">
                ${parseInt(results.implementationCost).toLocaleString()}
              </span>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-accent/10 rounded-lg">
              <span className="text-sm font-medium text-foreground">Net Annual Benefit</span>
              <span className="text-sm font-semibold text-accent">
                ${parseInt(results.netBenefit).toLocaleString()}
              </span>
            </div>
          </div>

          <div className="mt-6 p-4 bg-muted/20 rounded-lg">
            <h5 className="text-sm font-medium text-foreground mb-3">Benefit Breakdown</h5>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Efficiency Gains</span>
                <span className="text-foreground">${parseInt(results.efficiencyGains).toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Cost Reduction</span>
                <span className="text-foreground">${parseInt(results.costReduction).toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Risk Mitigation Value</span>
                <span className="text-foreground">${parseInt(results.riskMitigation).toLocaleString()}</span>
              </div>
            </div>
          </div>

          <div className="mt-6 p-4 bg-primary/5 border border-primary/20 rounded-lg">
            <p className="text-xs text-muted-foreground leading-relaxed">
              * These calculations are based on industry benchmarks and actual client outcomes. Individual results may vary based on implementation scope, organizational readiness, and specific use cases. Schedule a consultation for a detailed assessment.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ROICalculator;